<template>
    <pendo-date-picker
        :value="date"
        type="range"
        :locale-code="hasLocalizationUiSegmentFlag ? $locale() : undefined"
        :columns="dateRangeColumns"
        :shortcuts="filteredShortcuts"
        :available-dates="availableDates"
        :append-to-body="appendToBody"
        :disabled="disabled"
        :loading="!timeSeries || loading"
        :min-date="minDate"
        :max-date="maxDate"
        :min-trigger-width="minTriggerWidth"
        :max-trigger-width="maxTriggerWidth"
        :full-width="fullWidth"
        :trigger-size="triggerSize"
        :aria-label="ariaLabel"
        @drag="drag"
        @select-shortcut="updateShortcut"
        @change="updateDate">
        <template
            #trigger
            v-if="useDataMeasureTrigger">
            <pendo-date-picker-data-measure-trigger />
        </template>
        <template #value="{ value }">
            <slot
                name="value"
                v-bind="{ value }">
                <span v-if="isCustom">
                    {{ label }}
                </span>
            </slot>
        </template>
        <template #shortcut="{ shortcut }">
            <slot
                name="shortcut"
                v-bind="{ shortcut }" />
            <div
                v-pendo-tooltip="shortcut.tooltip"
                :class="{ 'disabled-shortcut-option': shortcut.disabled }"
                :disabled="shortcut.disabled">
                {{ shortcut.label }}
            </div>
        </template>
    </pendo-date-picker>
</template>

<script>
import { PendoDatePicker, PendoDatePickerDataMeasureTrigger, PendoTooltip } from '@pendo/components';
import moment from '@utils/moment-wrapper';
import cloneDeep from 'lodash/cloneDeep';
import {
    getDefaultShortcuts,
    formatDates,
    createTimeSeriesFromDate,
    formatStartEndDates,
} from './time-series-picker.util';
import { mapGetters } from 'vuex';

export default {
    name: 'TimeSeriesPicker',
    components: {
        PendoDatePicker,
        PendoDatePickerDataMeasureTrigger,
    },
    directives: { PendoTooltip },
    props: {
        appendToBody: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        timeSeries: {
            type: Object,
            required: true,
        },
        rangeLimiter: {
            type: Number,
            default: 0,
        },
        shortcuts: {
            type: [Array],
            default: () => getDefaultShortcuts(),
        },
        minDate: {
            type: String,
            default: null,
        },
        maxDate: {
            type: [String, Date],
            default: null,
        },
        triggerSize: {
            type: String,
            default: undefined,
        },
        minTriggerWidth: {
            type: [String, Number],
            default: undefined,
        },
        maxTriggerWidth: {
            type: [String, Number],
            default: undefined,
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        useDataMeasureTrigger: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        dateRangeColumns: {
            type: Number,
            default: 1,
        },
        ariaLabel: {
            type: String,
            default: null,
        },
    },
    emits: ['change', 'auto-update'],
    data () {
        return {
            spec: null,
            config: null,
            label: null,
            date: null,
            availableDates: null,
            dragValue: null,
            autoUpdate: false,
        };
    },
    computed: {
        ...mapGetters('auth', {
            hasSegmentFlag: 'hasSegmentFlag',
        }),
        hasLocalizationUiSegmentFlag () {
            return this.hasSegmentFlag('localizationUI');
        },
        filteredShortcuts () {
            if (!this.rangeLimiter) return this.shortcuts;

            return this.shortcuts.filter((shortcut) => {
                const numDays = Math.abs(shortcut.value.end - shortcut.value.start);

                return numDays < this.rangeLimiter;
            });
        },
        isCustom () {
            return this.config?.type === 'Custom';
        },
    },
    watch: {
        timeSeries () {
            this.populateFields();
        },
        dragValue (val, oldVal) {
            if (!val) {
                this.availableDates = null;
            } else if (this.rangeLimiter && val && !oldVal) {
                const rangeLimiterInclusive = this.rangeLimiter - 1;
                /*
                    rangeLimiterInclusive is one less than the rangeLimiter since setting the rangeLimiter to 7
                    should only allow us to choose a 7 day window (including the start date) not 7 days away from
                    the start date which would equal 8 days total.
                */
                const start = moment(val.start, { ignoreTimeZone: true })
                    .add(rangeLimiterInclusive * -1, 'days')
                    .toDate();
                const end = moment(val.start, { ignoreTimeZone: true })
                    .add(rangeLimiterInclusive, 'days')
                    .toDate();
                this.availableDates = { start, end };
            }
        },
    },
    created () {
        this.populateFields();
    },
    methods: {
        populateFields () {
            if (!this.timeSeries) return;

            // Check if we should emit auto-update BEFORE processing
            const shouldAutoUpdate = this.autoUpdate;
            if (shouldAutoUpdate) {
                this.autoUpdate = false;
            }

            this.config = cloneDeep(this.timeSeries.config);
            this.spec = cloneDeep(this.timeSeries.spec);
            let label = cloneDeep(this.timeSeries.label);
            this.date = formatDates(this.config);
            if (this.isCustom) {
                const { start, end } = this.date;
                label = formatStartEndDates(start, end);
            }
            this.label = label;
            if (this.rangeLimiter && this.timeSeries?.spec?.count > this.rangeLimiter) {
                if (!this.isCustom) {
                    const shortcut = this.filteredShortcuts?.[this.filteredShortcuts.length - 1];

                    this.config.type = shortcut?.label;

                    this.updateDate({
                        start: moment(this.timeSeries.config.end)
                            .add(shortcut?.value?.end, 'days')
                            .toDate(),
                        end: moment(this.timeSeries.config.end)
                            .add(shortcut?.value?.start, 'days')
                            .toDate(),
                    });
                } else {
                    this.updateDate({
                        start: moment(this.timeSeries.config.end)
                            .add(this.rangeLimiter * -1, 'days')
                            .toDate(),
                        end: moment(this.timeSeries.config.end)
                            .add(-1, 'days')
                            .toDate(),
                    });
                }

                this.autoUpdate = true;
            }

            if (shouldAutoUpdate) {
                this.$emit('auto-update');
            }
        },
        update () {
            this.$emit('change', {
                spec: this.spec,
                label: this.label,
                config: this.config,
            });
        },
        updateShortcut (shortcut) {
            this.config.type = shortcut.type ? shortcut.type : shortcut.label;
        },
        updateDate (date) {
            this.date = date;

            const { config, spec, label } = createTimeSeriesFromDate(date, false, true);
            this.config.start = config.start;
            this.config.end = config.end;
            if (this.dragValue) this.config.type = 'Custom';
            this.spec = spec;
            this.label = label;

            this.update();
        },
        drag (dragVal) {
            this.dragValue = dragVal;
        },
    },
};
</script>

<style lang="less" scoped>
:deep(.is-disabled.pendo-date-picker__trigger--data-measure:hover:after) {
    border: none;
}
</style>
